import React, { useState, useEffect } from 'react';
import { MapPin, Star, Shield, Users, Clock, Phone, ExternalLink, Navigation, Coffee, Flower2, Waves, Loader2, UtensilsCrossed, Store, Palmtree } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useTheme, API } from '../App';
import axios from '../lib/axios';

// Icon mapping for hotspots
const ICON_MAP = {
  coffee: Coffee,
  flower: Flower2,
  waves: Waves,
  star: Star,
  food: UtensilsCrossed,
  store: Store,
  palm: Palmtree,
};

// Get icon component from string
const getIconComponent = (iconName) => {
  return ICON_MAP[iconName] || Star;
};

// Curated hotspots data - Edit this to update locations
const HOTSPOTS = [
  // CURAÇAO
  {
    id: 'curacao-1',
    name: 'Punda Handelskade',
    type: 'meetup',
    island: 'Curaçao',
    city: 'Willemstad',
    address: 'Handelskade, Punda',
    description: 'Central meeting point in the heart of Willemstad. Safe, public area with plenty of parking nearby. Popular spot for vehicle and electronics exchanges.',
    ratings: {
      safety: 5,
      activity: 5,
      parking: 4,
      accessibility: 5,
    },
    features: ['Public Area', 'CCTV Coverage', 'Parking Available', 'Well-Lit'],
    hours: '8:00 AM - 6:00 PM',
    phone: null,
    website: null,
    image: 'https://images.unsplash.com/photo-1724597837785-09f3f1c04e51?w=600',
    coordinates: { lat: 12.1050, lon: -68.9350 },
    recommended_for: ['Vehicles', 'Electronics', 'Large Items'],
  },
  {
    id: 'curacao-2',
    name: 'Sambil Mall',
    type: 'meetup',
    island: 'Curaçao',
    city: 'Willemstad',
    address: 'Sambil Mall, Santa Rosa',
    description: 'Large shopping center with security. Meet inside near the food court for safe transactions. Air-conditioned and family-friendly.',
    ratings: {
      safety: 5,
      activity: 4,
      parking: 5,
      accessibility: 5,
    },
    features: ['Security Guards', 'Indoor', 'Food Court', 'Free Parking'],
    hours: '10:00 AM - 9:00 PM',
    phone: '+599 9 738 8888',
    website: 'https://sambil.cw',
    image: 'https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/pporyyj2_asdasdasdffffff.jpg',
    coordinates: { lat: 12.1300, lon: -68.9700 },
    recommended_for: ['Electronics', 'Fashion', 'Small Items'],
  },
  {
    id: 'curacao-3',
    name: 'Jan Thiel Beach Parking',
    type: 'meetup',
    island: 'Curaçao',
    city: 'Jan Thiel',
    address: 'Jan Thiel Beach Boulevard',
    description: 'Popular beach area with large parking lot. Great for boat and watersports equipment exchanges. Busy on weekends.',
    ratings: {
      safety: 4,
      activity: 4,
      parking: 5,
      accessibility: 4,
    },
    features: ['Public Area', 'Large Parking', 'Beach Access', 'Restaurants Nearby'],
    hours: '7:00 AM - 7:00 PM',
    phone: null,
    website: null,
    image: 'https://images.unsplash.com/photo-1724598170629-7e264f14f6de?w=600',
    coordinates: { lat: 12.0850, lon: -68.8650 },
    recommended_for: ['Boats', 'Watersports', 'Outdoor Gear'],
  },
  
  // ARUBA
  {
    id: 'aruba-1',
    name: 'Renaissance Mall',
    type: 'meetup',
    island: 'Aruba',
    city: 'Oranjestad',
    address: 'L.G. Smith Boulevard 82',
    description: 'Upscale shopping center in downtown Oranjestad. Security on-site. Perfect for high-value item exchanges.',
    ratings: {
      safety: 5,
      activity: 4,
      parking: 3,
      accessibility: 5,
    },
    features: ['Security', 'Indoor', 'Central Location', 'Near Police Station'],
    hours: '10:00 AM - 7:00 PM',
    phone: '+297 582 4622',
    website: null,
    image: 'https://images.unsplash.com/photo-1680383829805-884113816b3f?w=600',
    coordinates: { lat: 12.5092, lon: -70.0086 },
    recommended_for: ['Jewelry', 'Electronics', 'Luxury Items'],
  },
  {
    id: 'aruba-2',
    name: 'Palm Beach Plaza',
    type: 'meetup',
    island: 'Aruba',
    city: 'Palm Beach',
    address: 'J.E. Irausquin Blvd 95',
    description: 'Busy tourist area with lots of foot traffic. Multiple cafes for casual meetups. Well-lit area with good security presence.',
    ratings: {
      safety: 5,
      activity: 5,
      parking: 4,
      accessibility: 5,
    },
    features: ['High Traffic', 'Cafes', 'Well-Lit', 'Tourist Police'],
    hours: '9:00 AM - 10:00 PM',
    phone: null,
    website: null,
    image: 'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=600',
    coordinates: { lat: 12.5750, lon: -70.0550 },
    recommended_for: ['General Items', 'Fashion', 'Accessories'],
  },
  {
    id: 'aruba-3',
    name: 'San Nicolas Art District',
    type: 'meetup',
    island: 'Aruba',
    city: 'San Nicolas',
    address: 'Main Street, San Nicolas',
    description: 'Colorful art district in the south. Great for creative goods and local crafts. More relaxed atmosphere than tourist areas.',
    ratings: {
      safety: 4,
      activity: 3,
      parking: 4,
      accessibility: 4,
    },
    features: ['Art Murals', 'Local Vibe', 'Street Parking', 'Cafes'],
    hours: '9:00 AM - 5:00 PM',
    phone: null,
    website: null,
    image: 'https://images.unsplash.com/photo-1655484316990-e5f2be42fc50?w=600',
    coordinates: { lat: 12.4372, lon: -69.9030 },
    recommended_for: ['Art', 'Crafts', 'Vintage Items'],
  },
  
  // BONAIRE
  {
    id: 'bonaire-1',
    name: 'Kralendijk Waterfront',
    type: 'meetup',
    island: 'Bonaire',
    city: 'Kralendijk',
    address: 'Kaya Grandi, Kralendijk',
    description: 'Main street of Bonaire\'s capital. Safe, central location with restaurants and shops. Ideal for any type of exchange.',
    ratings: {
      safety: 5,
      activity: 4,
      parking: 3,
      accessibility: 5,
    },
    features: ['Central', 'Restaurants', 'Banks Nearby', 'Public Area'],
    hours: '8:00 AM - 6:00 PM',
    phone: null,
    website: null,
    image: 'https://images.unsplash.com/photo-1712960258915-7ab4eb27d24f?w=600',
    coordinates: { lat: 12.1443, lon: -68.2655 },
    recommended_for: ['All Items', 'Diving Gear', 'Vehicles'],
  },
  {
    id: 'bonaire-2',
    name: 'Warehouse Bonaire',
    type: 'meetup',
    island: 'Bonaire',
    city: 'Kralendijk',
    address: 'Kaya Industria',
    description: 'Large supermarket with big parking lot. Security cameras throughout. Good for quick, safe exchanges any day of the week.',
    ratings: {
      safety: 5,
      activity: 4,
      parking: 5,
      accessibility: 5,
    },
    features: ['CCTV', 'Large Parking', 'Open Daily', 'Well-Lit'],
    hours: '8:00 AM - 8:00 PM',
    phone: '+599 717 8220',
    website: null,
    image: 'https://images.unsplash.com/photo-1772458251839-cbd3ef8b7018?w=600',
    coordinates: { lat: 12.1550, lon: -68.2800 },
    recommended_for: ['General Items', 'Quick Exchanges', 'Evening Meetups'],
  },
];

// Island color helper
const getIslandColor = (island) => {
  switch (island) {
    case 'Aruba': return 'bg-blue-500';
    case 'Bonaire': return 'bg-amber-500';
    case 'Curaçao': return 'bg-emerald-500';
    default: return 'bg-primary';
  }
};

// Type icon helper
const getTypeIcon = (type) => {
  return MapPin;
};

const HotspotsPage = () => {
  const { theme } = useTheme();
  const [selectedIsland, setSelectedIsland] = useState(null);
  const [featuredPartners, setFeaturedPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(true);

  // Fetch featured partners from API
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await axios.get(`${API}/hotspots`);
        setFeaturedPartners(response.data.hotspots || []);
      } catch (error) {
        console.error('Error fetching hotspots:', error);
      } finally {
        setLoadingPartners(false);
      }
    };
    fetchPartners();
  }, []);

  const filteredHotspots = HOTSPOTS.filter(spot => {
    if (selectedIsland && spot.island !== selectedIsland) return false;
    return true;
  });

  const islands = ['Aruba', 'Bonaire', 'Curaçao'];

  return (
    <div className="min-h-screen bg-background" data-testid="hotspots-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary/20 via-background to-background py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-6 h-6 text-emerald-500" />
              <span className="text-primary font-medium">Safe Trading</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Your Buy Site Hotspots
            </h1>
            <p className="text-lg text-muted-foreground">
              Discover our curated list of safe meeting locations across 
              Aruba, Bonaire, and Curaçao.
            </p>
          </div>
        </div>
      </div>


      {/* Safe Meeting Locations Section Header */}
      <div className="container mx-auto px-4 pt-12 pb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Safe Meeting Locations</h2>
            <p className="text-sm text-muted-foreground">Recommended spots for buyer-seller meetups</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-background border-b border-border py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2 items-center">
            {/* Island Filter */}
            {islands.map((island) => (
              <button
                key={island}
                onClick={() => setSelectedIsland(island)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedIsland === island
                    ? 'bg-primary text-white'
                    : 'bg-secondary text-foreground hover:bg-secondary/80'
                }`}
              >
                {island}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hotspots Grid */}
      <div className="container mx-auto px-4 py-8">
        <p className="text-muted-foreground mb-6">
          Showing {filteredHotspots.length} location{filteredHotspots.length !== 1 ? 's' : ''}
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredHotspots.map((spot) => {
            return (
              <div
                key={spot.id}
                className="bg-card rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`hotspot-${spot.id}`}
              >
                {/* Image */}
                <div className="relative h-48">
                  <img
                    src={spot.image}
                    alt={spot.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={`${getIslandColor(spot.island)} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
                      {spot.island}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-foreground mb-1">{spot.name}</h3>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mb-3">
                    <MapPin className="w-4 h-4" />
                    {spot.city}, {spot.island}
                  </p>
                  
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {spot.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {spot.features.slice(0, 4).map((feature, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-secondary text-foreground px-2 py-1 rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* Hours & Contact */}
                  <div className="flex items-center justify-between text-sm text-muted-foreground pt-3 border-t border-border">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {spot.hours}
                    </div>
                    <div className="flex gap-2">
                      {spot.phone && (
                        <a
                          href={`tel:${spot.phone}`}
                          className="p-2 hover:bg-secondary rounded-lg transition-colors"
                          title="Call"
                        >
                          <Phone className="w-4 h-4" />
                        </a>
                      )}
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${spot.coordinates.lat},${spot.coordinates.lon}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-secondary rounded-lg transition-colors"
                        title="Get Directions"
                      >
                        <Navigation className="w-4 h-4" />
                      </a>
                    </div>
                  </div>

                  {/* Recommended For */}
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium">Best for:</span>{' '}
                      {spot.recommended_for.join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredHotspots.length === 0 && (
          <div className="text-center py-16">
            <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No hotspots found</h3>
            <p className="text-muted-foreground">Try adjusting your filters</p>
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="container mx-auto px-4 py-12 text-center">
        <h3 className="text-xl font-semibold text-foreground mb-3">
          Know a great spot we should add?
        </h3>
        <p className="text-muted-foreground mb-6">
          Help our community grow by suggesting safe meetup locations
        </p>
        <a
          href="mailto:info@yourbuysite.com?subject=Hotspot%20Location%20Suggestion"
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors"
        >
          Suggest a Location
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <Footer />
    </div>
  );
};

export default HotspotsPage;
